import type { Resource } from '@angular/core';

import type { ErrorState } from '@shared/models/async-state.types';

export const SESSION_EXPIRED_MESSAGE = 'Sua sessão expirou. Entre novamente.';

const CONFLICT_STATUS = 409;

const TOO_MANY_REQUESTS_STATUS = 429;

const REFUSAL_STATUSES: ReadonlySet<number> = new Set([400, 403, 404, CONFLICT_STATUS, 422]);

export function errorFromHttpStatus(status: number, reference?: string): ErrorState {
  switch (true) {
    case status === 401:
      return {
        kind: 'permission',
        message: SESSION_EXPIRED_MESSAGE,
        canRetry: false,
        reference,
      };

    case status === 403:
      return {
        kind: 'permission',
        message: 'Você não tem permissão para ver estes dados.',
        canRetry: false,
        hint: 'Se você acredita que deveria ter acesso, fale com o administrador da sua conta.',
        reference,
      };

    case status === 404:
      return {
        kind: 'not-found',
        message: 'Este registro não existe mais.',
        canRetry: false,
        reference,
      };

    case status === 408 || status === 504:
      return {
        kind: 'timeout',
        message: 'A consulta demorou demais. Tente filtrar por um período menor.',
        canRetry: true,
        reference,
      };

    case status === 0:
      return {
        kind: 'network',
        message: 'Você está sem conexão com o servidor.',
        canRetry: true,
        hint: 'Verifique sua conexão e tente novamente.',
        reference,
      };

    default:
      return {
        kind: 'server',
        message: 'Não foi possível carregar os dados.',
        canRetry: true,
        hint: 'O problema é do nosso lado. Tente de novo em alguns instantes.',
        reference,
      };
  }
}

export const NOT_FOUND_ERROR = errorFromHttpStatus(404);

export function errorOfResource(resource: Pick<Resource<unknown>, 'error'>): ErrorState | null {
  const failure = resource.error();

  return failure === undefined ? null : errorFromResponse(failure);
}

export function errorFromResponse(failure: unknown, reference?: string): ErrorState {
  return errorFromHttpStatus(statusFromFailure(failure) ?? 500, reference);
}

export function statusFromFailure(failure: unknown): number | null {
  if (!failure || typeof failure !== 'object') {
    return null;
  }

  const raw = (failure as { status?: unknown }).status;

  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }

  if (typeof raw !== 'string' || raw.trim() === '') {
    return null;
  }

  const parsed = Number(raw);

  return Number.isFinite(parsed) ? parsed : null;
}

export function isRefusal(failure: unknown): boolean {
  const status = statusFromFailure(failure);

  return status !== null && REFUSAL_STATUSES.has(status);
}

export function isConflict(failure: unknown): boolean {
  return statusFromFailure(failure) === CONFLICT_STATUS;
}

export function isThrottled(failure: unknown): boolean {
  return statusFromFailure(failure) === TOO_MANY_REQUESTS_STATUS;
}
